import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  PccToken,
  PccVehicle,
  StaticVehicleCombustible,
  VehicleDescription,
  Vehicles
} from 'src/app/shared/model/vehicle.model';
import { ModelList } from 'src/app/shared/model/order.model';
import { LoanOptions } from 'src/app/shared/model/loanRequest.enum';

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  urlVehicle =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/vehicle-service/v1/vehicles`
      : `${environment.API_CONTEXT}/vehicle-service/v1/vehicles`;
  urlVehicleRetribution = this.urlVehicle + '/retribution';
  urlRedPlates =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/vehicle-service/red-plates`
      : `${environment.API_CONTEXT}/vehicle-service/red-plates`;
  urlCampaigns =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/vehicle-service/v1/campaigns`
      : `${environment.API_CONTEXT}/vehicle-service/v1/campaigns`;
  urlValidations = this.urlVehicle + '/validations';
  urlLeasing =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/vehicle-service/pcc/leasing`
      : `${environment.API_CONTEXT}/vehicle-service/pcc/leasing`;

  private vehicleDescriptions: VehicleDescription = {
    KJ15WX: 'IBIZA 1.0 TSI 85kW (115CV) Start&Stop FR XS',
    KJ75WX: 'ARONA 1.0 TSI 85kW (115CV) St&Sp FR XM',
    KJ75WZ: 'ARONA 1.0 TSI 85kW (115CV) DSG-7 St&Sp FR XM',
    KLG24X: 'LEON 5P 1.5 TSI 85 kW (115cv) St&Sp STYLE XS',
    KLG5LZ: 'LEON 5P 2.0 TDI CR 110kW (150cv) DSG-7 St&Sp FR XS',
    KLD5LZ: 'LEON SP 2.0 TDI CR 110kW (150cv) DSG-7 St&Sp FR XS',
    KHP2WX: 'ATECA 1.0 TSI 85kW (115CV) St&Sp Style EVO EDITION',
    KHP5NZ: 'ATECA 1.5 EcoTSI 110kW (150CV) DSG-7 St&Sp FR SPECIAL EDITION',
    KUGB5Z: 'CUPRA LEON 1.5 eTSI 110 KW (150 CV) DSG 7 vel',
    KUGB0Y: 'CUPRA LEON 1.5 TSI e-HYBRID 150 KW (204 CV) DSG 6 vel e',
    KUDB5Z: 'CUPRA LEON SPORTSTOURER 1.5 eTSI 110 KW (150 CV) DSG 7 vel',
    KUDB0Y:
      'CUPRA LEON SPORTSTOURER 1.5 TSI e-HYBRID 150 KW (204 CV) DSG 6 vel',
    KMPB5Z: 'CUPRA FORMENTOR 1.5 eTSI 110 KW (150 CV) DSG 7 vel',
    KMPBNX: 'CUPRA FORMENTOR 1.5 TSI 110 KW (150 CV)',
    KMPB0Y: 'CUPRA FORMENTOR 1.5 e-HYBRID 150 KW (204 CV) DSG 6 vel',
    K11C5G: 'CUPRA BORN 170kW (231CV) 59kWh e-Boost Pack Advanced Plus',
    KP1B8Y: 'CUPRA Terramar 1.5 e-Hybrid 150kW (204CV) DSG',
    KJP5WZ: 'Arona 1.0 TSI 85 KW (115 CV) DSG Start/Stop FR +',
    KJG5WX: 'IBIZA 1.0 TSI 85kW (115CV) Start&Stop FR+',
    KJP5WX: 'ARONA 1.0 TSI 85kW (115CV) Start&Stop FR+'
  };

  private staticVehicleCombustible: StaticVehicleCombustible = {
    KJ15WX: 'Gasolina',
    KJ75WX: 'Gasolina',
    KJ75WZ: 'Gasolina',
    KLG24X: 'Gasolina',
    KLG5LZ: 'Diesel',
    KLD5LZ: 'Diesel',
    KHP2WX: 'Gasolina',
    KHP5NZ: 'Gasolina',
    KUGB5Z: 'Gasolina',
    KUGB0Y: 'e-HYBRID',
    KUDB5Z: 'Gasolina',
    KUDB0Y: 'e-HYBRID',
    KMPB5Z: 'Gasolina',
    KMPBNX: 'Gasolina',
    KMPB0Y: 'e-HYBRID',
    K11C5G: 'Eléctrico',
    KP1B8Y: 'Gasolina',
    KJP5WZ: 'Gasolina',
    KJG5WX: 'Gasolina',
    KJP5WX: 'Gasolina'
  };

  constructor(private http: HttpClient) {}

  getVehiclesByVehicleTypeAndNIS(
    vehicleType: string,
    orderType?: string
  ): Observable<any> {
    if (!!orderType) {
      return this.http.get(
        `${this.urlVehicle}/nis?vehicleType=${vehicleType}&orderType=${orderType}`
      );
    } else {
      return this.http.get(`${this.urlVehicle}/nis?vehicleType=${vehicleType}`);
    }
  }

  getVehiclesRetribution(
    nis: string,
    vinSubs: string | null,
    vehicleType: string
  ): Observable<any> {
    return this.http.post(`${this.urlVehicleRetribution}`, {
      nis,
      vinSubs,
      vehicleType
    });
  }

  getPccVehicle(seatCode: string) {
    const params = { seatCode };
    return this.http
      .get<PccVehicle>(`${this.urlVehicle}/vehicle-configuration`, { params })
      .pipe(
        tap((resp) => {
          return resp;
        })
      );
  }

  getStockModels() {
    return this.http.get<ModelList[]>(`${this.urlVehicle}/stock-models`);
  }

  getVehiclesDepartment(department: string, vehicleType: string) {
    const params = new HttpParams()
      .set('vehicleType', vehicleType)
      .set('department', department);
    return this.http.get<Vehicles[]>(`${this.urlVehicle}/department`, {
      params: params
    });
  }

  getValidations(tmaimg: string, requestType: string): Observable<any> {
    const params = new HttpParams()
      .set('tmaimg', tmaimg)
      .set('requestType', requestType);
    return this.http.get<any>(this.urlValidations, { params });
  }

  getStockVehicles(
    vehicleType: string,
    center?: string,
    deliveryDate?: string,
    returnDate?: string
  ) {
    let params = new HttpParams().set(
      'vehicleType',
      center ? LoanOptions.CP.toUpperCase() : vehicleType
    );

    if (center && deliveryDate && returnDate) {
      params = params
        .set('carPoolCenter', center)
        .set('deliveryDateTime', deliveryDate)
        .set('returnDateTime', returnDate);
    }
    return this.http.get<Vehicles[]>(`${this.urlVehicle}/stock`, { params });
  }

  getVoCSV(vehicleType: string): Observable<Blob> {
    const params = new HttpParams().set('vehicleType', vehicleType);

    return this.http.get(`${this.urlVehicle}/stock/csv`, {
      params,
      responseType: 'blob'
    });
  }

  getVehiclesVinOrPlate(vin?: string, plateNumber?: string) {
    let params;
    if (vin) {
      params = new HttpParams().set('VIN', vin!);
    } else {
      params = new HttpParams().set('plateNumber', plateNumber!);
    }

    return this.http.get<any>(`${this.urlVehicle}`, {
      params: params
    });
  }

  getVehiclesVinOrPlateES(vin?: string, plateNumber?: string) {
    let params;
    if (vin) {
      params = new HttpParams().set('VIN', vin!);
    } else {
      params = new HttpParams().set('plateNumber', plateNumber!);
    }

    return this.http.get<any>(`${this.urlVehicle}/es`, {
      params: params
    });
  }

  getVehiclesProjects(vin?: string, plateNumber?: string) {
    let params;
    if (vin) {
      params = new HttpParams().set('VIN', vin!);
    } else {
      params = new HttpParams().set('plateNumber', plateNumber!);
    }

    return this.http.get<any>(`${this.urlVehicle}/projects`, {
      params: params
    });
  }

  getVehiclesRedPlate(redPlateNumberLike: string) {
    const params = new HttpParams().set(
      'redPlateNumberLike',
      redPlateNumberLike
    );

    return this.http.get<any>(`${this.urlRedPlates}`, {
      params: params
    });
  }

  getCampaignsVehicles(category: string) {
    const params = new HttpParams().set('category', category);

    return this.http.get<any>(`${this.urlCampaigns}`, {
      params: params
    });
  }

  getConfigurationVip(seatCode: string) {
    const params = { seatCode };
    return this.http
      .get<PccVehicle>(`${this.urlVehicle}/vehicle-configuration-vip`, {
        params
      })
      .pipe(
        tap((resp) => {
          return resp;
        })
      );
  }

  getPccLeasingToken() {
    return this.http.get<PccToken>(`${this.urlLeasing}/token`).pipe(
      tap((resp) => {
        return resp;
      })
    );
  }

  getLeasingRetribution(tmaimg: string): Observable<any> {
    const params = new HttpParams().set('tmaimg', tmaimg);
    return this.http.get<any>(`${this.urlVehicle}/leasing-retribution`, {
      params
    });
  }

  getModelByTmaimg(tmaimg: string): string {
    return this.vehicleDescriptions[tmaimg] ?? '';
  }

  getCombustibleByTmaimg(tmaimg: string): string {
    return this.staticVehicleCombustible[tmaimg] ?? '';
  }
}
